import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Helmet from '../components/Helmet/Helmet';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase.config';
import useAuth from '../custom-hooks/useAuth';
import {
  getAllOrders,
  updateOrderStatus,
  bulkUpdateOrderStatus,
  deleteOrder,
  getUsersList,
  updateUserRole,
  getAllProducts,
  saveProduct,
  deleteProduct,
  getStoreSettings,
  updateStoreSettings,
} from '../services/adminService';
import {
  getCoupons,
  createCoupon,
  toggleCouponStatus,
  deleteCoupon,
} from '../services/couponService';
import OrderTracker from '../components/Ui/OrderTracker';
import InvoiceModal from '../components/Ui/InvoiceModal';
import { getProductImg, handleImgError } from '../utils/imageHelper';
import '../styles/admin.css';

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Active Tab: 'overview' | 'orders' | 'products' | 'clients' | 'settings'
  const [activeTab, setActiveTab] = useState('overview');

  // Master Data States
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [storeSettings, setStoreSettings] = useState({
    storeStatus: 'online',
    whiteGloveDelivery: 'active',
    freeShippingThreshold: 500,
    announcementText: 'Complimentary White-Glove Delivery Across Egypt & Overseas Commissions',
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Orders Filter, Date Range & Sorting
  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderDateRange, setOrderDateRange] = useState('all'); // 'all' | 'today' | '7d' | 'month'
  const [orderSortBy, setOrderSortBy] = useState('newest');
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  // Bulk Operations State
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);

  // Products Filter & Search
  const [productSearch, setProductSearch] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');

  // Chart Timeframe: '7d' | '30d' | '1y'
  const [chartTimeframe, setChartTimeframe] = useState('30d');

  // Modals & Drawers
  const [selectedInvoiceOrder, setSelectedInvoiceOrder] = useState(null);
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [courierDispatchOrder, setCourierDispatchOrder] = useState(null);

  // Courier Dispatch Form State
  const [courierName, setCourierName] = useState('Atelier White-Glove');
  const [trackingNumber, setTrackingNumber] = useState('');

  // Product Form State (for both Add and Edit)
  const [productFormData, setProductFormData] = useState({
    productName: '',
    category: 'sofa',
    price: '',
    material: '',
    shortDesc: '',
    description: '',
    imgUrl: '',
    stockQuantity: 15,
    inStock: true,
  });

  // VIP Promo Vouchers State
  const [coupons, setCoupons] = useState([]);
  const [couponFormData, setCouponFormData] = useState({
    code: '',
    type: 'percentage',
    value: 15,
    minOrderAmount: 0,
    maxDiscount: 250,
    description: '',
  });

  // Real-time Sound Alert & Radar Chime
  const [soundEnabled, setSoundEnabled] = useState(true);

  const playLuxuryChime = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(587.33, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.14);
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 1.2);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 1.2);
    } catch (e) {
      // Audio autoplay policy
    }
  }, [soundEnabled]);

  // Current Live Clock
  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(
        new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      );
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch all Admin Data from Firestore & Cloud Services
  const loadDashboardData = useCallback(async () => {
    try {
      setRefreshing(true);
      const [ordersData, usersData, productsData, settingsData, couponsData] = await Promise.all([
        getAllOrders().catch(() => []),
        getUsersList().catch(() => []),
        getAllProducts().catch(() => []),
        getStoreSettings().catch(() => null),
        getCoupons().catch(() => []),
      ]);
      setOrders(ordersData);
      setUsers(usersData);
      setProducts(productsData);
      setCoupons(couponsData);
      if (settingsData) setStoreSettings(settingsData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Could not refresh store data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Live Order Radar (Periodic sync & sound alert on incoming orders)
  useEffect(() => {
    const radar = setInterval(async () => {
      try {
        const freshOrders = await getAllOrders();
        if (freshOrders && freshOrders.length > orders.length && orders.length > 0) {
          playLuxuryChime();
          const newest = freshOrders[0];
          toast.success(
            `✨ New Haute-Couture Commission Placed: #${newest.orderNumber || 'ORD'} ($${newest.totalAmount})`,
            { autoClose: 7000 }
          );
          setOrders(freshOrders);
        }
      } catch (e) {
        // silent fallback
      }
    }, 12000);
    return () => clearInterval(radar);
  }, [orders.length, playLuxuryChime]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Master KPI Calculations
  const stats = useMemo(() => {
    const totalRevenue = orders.reduce((sum, o) => sum + Number(o.totalAmount || 0), 0);
    const totalOrdersCount = orders.length;
    const pendingOrdersCount = orders.filter((o) => (o.status || '').toLowerCase() === 'pending').length;
    const processingCount = orders.filter((o) => (o.status || '').toLowerCase() === 'processing').length;
    const shippedCount = orders.filter((o) => (o.status || '').toLowerCase() === 'shipped').length;
    const deliveredCount = orders.filter((o) => (o.status || '').toLowerCase() === 'delivered').length;
    const cancelledCount = orders.filter((o) => (o.status || '').toLowerCase() === 'cancelled').length;
    const avgOrderValue = totalOrdersCount > 0 ? Math.round(totalRevenue / totalOrdersCount) : 480;
    const deliveryRate = totalOrdersCount > 0 ? Math.round((deliveredCount / totalOrdersCount) * 100) : 96;

    // Current Month Revenue
    const now = new Date();
    const currentMonthOrders = orders.filter((o) => {
      if (!o.createdAt) return false;
      const d = new Date(o.createdAt);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });
    const currentMonthRevenue = currentMonthOrders.reduce((sum, o) => sum + Number(o.totalAmount || 0), 0);
    const monthlyGoal = 35000;
    const monthlyProgress = Math.min(Math.round((currentMonthRevenue / monthlyGoal) * 100), 100);

    return {
      revenue: totalRevenue,
      orders: totalOrdersCount,
      pending: pendingOrdersCount,
      processing: processingCount,
      shipped: shippedCount,
      delivered: deliveredCount,
      cancelled: cancelledCount,
      avgOrderValue,
      deliveryRate,
      clients: users.length > 0 ? users.length : 14,
      products: products.length,
      currentMonthRevenue,
      monthlyGoal,
      monthlyProgress,
    };
  }, [orders, users, products]);

  // Dynamic Category Sales Breakdown from Real Orders
  const categoryStats = useMemo(() => {
    const categories = {
      sofa: { name: 'Sofas & Sectionals', revenue: 0, itemsCount: 0, color: 'linear-gradient(90deg, #d4af37, #f59e0b)' },
      table: { name: 'Dining & Coffee Tables', revenue: 0, itemsCount: 0, color: 'linear-gradient(90deg, #0284c7, #38bdf8)' },
      chair: { name: 'Armchairs & Lounge', revenue: 0, itemsCount: 0, color: 'linear-gradient(90deg, #059669, #34d399)' },
      lighting: { name: 'Designer Lighting', revenue: 0, itemsCount: 0, color: 'linear-gradient(90deg, #9333ea, #c084fc)' },
      decor: { name: 'Home Accents & Decor', revenue: 0, itemsCount: 0, color: 'linear-gradient(90deg, #ec4899, #f43f5e)' },
    };

    let totalCategorizedRevenue = 0;

    orders.forEach((ord) => {
      (ord.items || []).forEach((item) => {
        const cat = (item.category || 'sofa').toLowerCase();
        const itemTotal = Number(item.totalPrice || item.price * (item.quantity || 1) || 0);
        if (categories[cat]) {
          categories[cat].revenue += itemTotal;
          categories[cat].itemsCount += item.quantity || 1;
        } else {
          categories.sofa.revenue += itemTotal;
          categories.sofa.itemsCount += item.quantity || 1;
        }
        totalCategorizedRevenue += itemTotal;
      });
    });

    // If orders revenue is zero, provide sensible baseline projection for visual preview
    if (totalCategorizedRevenue === 0) {
      return [
        { key: 'sofa', name: 'Sofas & Sectionals', revenue: 14200, percent: 38, color: categories.sofa.color },
        { key: 'table', name: 'Dining & Coffee Tables', revenue: 9800, percent: 26, color: categories.table.color },
        { key: 'chair', name: 'Armchairs & Lounge', revenue: 6400, percent: 18, color: categories.chair.color },
        { key: 'lighting', name: 'Designer Lighting', revenue: 4200, percent: 12, color: categories.lighting.color },
      ];
    }

    return Object.entries(categories).map(([key, c]) => ({
      key,
      name: c.name,
      revenue: c.revenue,
      percent: totalCategorizedRevenue > 0 ? Math.round((c.revenue / totalCategorizedRevenue) * 100) : 0,
      color: c.color,
    }));
  }, [orders]);

  // Dynamic Revenue Velocity Chart Calculation based on Actual Orders
  const chartData = useMemo(() => {
    const pointsCount = 6;
    const now = Date.now();
    const spanMs =
      chartTimeframe === '7d'
        ? 7 * 24 * 60 * 60 * 1000
        : chartTimeframe === '30d'
        ? 30 * 24 * 60 * 60 * 1000
        : 365 * 24 * 60 * 60 * 1000;

    const bucketSize = spanMs / pointsCount;
    const buckets = Array(pointsCount).fill(0);

    orders.forEach((o) => {
      if (!o.createdAt) return;
      const orderTime = new Date(o.createdAt).getTime();
      const diff = now - orderTime;
      if (diff >= 0 && diff < spanMs) {
        const bucketIndex = Math.min(
          pointsCount - 1 - Math.floor(diff / bucketSize),
          pointsCount - 1
        );
        if (bucketIndex >= 0) {
          buckets[bucketIndex] += Number(o.totalAmount || 0);
        }
      }
    });

    // Fallback baseline curve if very few orders exist
    const hasData = buckets.some((v) => v > 0);
    const normalizedValues = hasData
      ? buckets
      : chartTimeframe === '7d'
      ? [1200, 2400, 1800, 3200, 4100, 5600]
      : chartTimeframe === '30d'
      ? [4200, 6800, 5900, 11200, 16400, 24800]
      : [18000, 29000, 44000, 62000, 89000, 128000];

    const maxVal = Math.max(...normalizedValues, 1000);
    const minVal = Math.min(...normalizedValues, 0);
    const height = 140;
    const baseY = 160;

    // Convert values into SVG coordinate points (x from 40 to 660, y from 160 to 30)
    const points = normalizedValues.map((val, idx) => {
      const x = 40 + idx * (620 / (pointsCount - 1));
      const normalizedRatio = maxVal > minVal ? (val - minVal) / (maxVal - minVal) : 0.5;
      const y = Math.round(baseY - normalizedRatio * height + 10);
      return { x, y, val };
    });

    // Build SVG Path
    let linePath = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];
      const cpX = (current.x + next.x) / 2;
      linePath += ` Q ${cpX} ${current.y}, ${next.x} ${next.y}`;
    }

    const areaPath = `${linePath} L ${points[points.length - 1].x} 180 L ${points[0].x} 180 Z`;

    const labels =
      chartTimeframe === '7d'
        ? ['D-6', 'D-5', 'D-4', 'D-3', 'D-2', 'Today']
        : chartTimeframe === '30d'
        ? ['WK 01', 'WK 02', 'WK 03', 'WK 04', 'WK 05', 'Live Pace']
        : ['Q1', 'Q2', 'Q3', 'Q4', 'Q5', 'Peak Year'];

    return { points, linePath, areaPath, labels };
  }, [orders, chartTimeframe]);

  // Real-time Status Changer with Courier Intercept
  const handleStatusSelectChange = async (orderId, newStatus) => {
    const targetOrder = orders.find((o) => o.id === orderId);

    // If changing to Shipped, prompt courier dispatch details
    if (newStatus === 'Shipped') {
      setCourierDispatchOrder(targetOrder);
      setCourierName(targetOrder?.courier || 'Atelier White-Glove');
      setTrackingNumber(targetOrder?.trackingNumber || '');
      return;
    }

    try {
      await updateOrderStatus(orderId, newStatus);
      setOrders((prev) =>
        prev.map((ord) => (ord.id === orderId ? { ...ord, status: newStatus } : ord))
      );
      toast.success(`Order #${orderId.slice(0, 6)} updated to ${newStatus}`);
    } catch {
      toast.error('Failed to update order status');
    }
  };

  // Submit Courier Tracking
  const handleCourierSubmit = async (e) => {
    e.preventDefault();
    if (!courierDispatchOrder) return;

    try {
      await updateOrderStatus(courierDispatchOrder.id, 'Shipped', {
        courier: courierName,
        trackingNumber: trackingNumber.trim(),
      });

      setOrders((prev) =>
        prev.map((ord) =>
          ord.id === courierDispatchOrder.id
            ? { ...ord, status: 'Shipped', courier: courierName, trackingNumber: trackingNumber.trim() }
            : ord
        )
      );

      toast.success(`Order #${courierDispatchOrder.orderNumber || courierDispatchOrder.id.slice(0, 6)} dispatched via ${courierName}!`);
      setCourierDispatchOrder(null);
    } catch {
      toast.error('Failed to save courier dispatch info');
    }
  };

  // Bulk Status Updates
  const handleBulkStatusChange = async (newStatus) => {
    if (selectedOrderIds.length === 0) return;

    try {
      await bulkUpdateOrderStatus(selectedOrderIds, newStatus);
      setOrders((prev) =>
        prev.map((ord) =>
          selectedOrderIds.includes(ord.id) ? { ...ord, status: newStatus } : ord
        )
      );
      toast.success(`Updated ${selectedOrderIds.length} orders to ${newStatus}`);
      setSelectedOrderIds([]);
    } catch {
      toast.error('Bulk update failed');
    }
  };

  // Toggle Order Selection for Bulk Actions
  const toggleSelectOrder = (orderId) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  const handleSelectAllOrders = () => {
    if (selectedOrderIds.length === filteredOrders.length) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(filteredOrders.map((o) => o.id));
    }
  };

  // Delete Order Record
  const handleDeleteOrder = async (orderId, orderNumber) => {
    if (window.confirm(`Are you sure you want to remove commission record #${orderNumber}?`)) {
      try {
        await deleteOrder(orderId);
        setOrders((prev) => prev.filter((ord) => ord.id !== orderId));
        setSelectedOrderIds((prev) => prev.filter((id) => id !== orderId));
        toast.info(`Order #${orderNumber} removed from database`);
      } catch {
        toast.error('Failed to delete order');
      }
    }
  };

  // Export Orders to CSV (handles both all and selected)
  const handleExportCSV = (exportSelected = false) => {
    const listToExport = exportSelected
      ? orders.filter((o) => selectedOrderIds.includes(o.id))
      : filteredOrders;

    if (listToExport.length === 0) {
      toast.info('No orders available to export.');
      return;
    }

    const headers = [
      'Order Number',
      'Date',
      'Customer Name',
      'Email',
      'Phone',
      'City',
      'Address',
      'Total Amount (USD)',
      'Status',
      'Courier',
      'Tracking Number',
      'Payment Method',
    ];

    const rows = listToExport.map((o) => [
      `"${o.orderNumber || o.id}"`,
      `"${o.createdAt ? new Date(o.createdAt).toLocaleDateString('en-US') : 'N/A'}"`,
      `"${(o.customerName || '').replace(/"/g, '""')}"`,
      `"${o.userEmail || o.email || ''}"`,
      `"${(o.phoneNumber || '').replace(/"/g, '""')}"`,
      `"${(o.shippingAddress?.city || '').replace(/"/g, '""')}"`,
      `"${(o.shippingAddress?.address || '').replace(/"/g, '""')}"`,
      o.totalAmount || 0,
      `"${o.status || 'Pending'}"`,
      `"${o.courier || ''}"`,
      `"${o.trackingNumber || ''}"`,
      `"${o.paymentMethod || 'COD'}"`,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,\uFEFF' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `LHABILLEUR_Commissions_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${listToExport.length} orders to CSV!`);
  };

  // Filter & Sort Orders (Search, Status, Date Range, Sort)
  const filteredOrders = useMemo(() => {
    const now = Date.now();

    return orders
      .filter((order) => {
        // Status Filter
        const matchesStatus =
          orderStatusFilter === 'all' ||
          (order.status || '').toLowerCase() === orderStatusFilter.toLowerCase();

        // Date Range Filter
        let matchesDate = true;
        if (orderDateRange !== 'all' && order.createdAt) {
          const orderTime = new Date(order.createdAt).getTime();
          const diffMs = now - orderTime;
          if (orderDateRange === 'today') {
            matchesDate = diffMs <= 24 * 60 * 60 * 1000;
          } else if (orderDateRange === '7d') {
            matchesDate = diffMs <= 7 * 24 * 60 * 60 * 1000;
          } else if (orderDateRange === 'month') {
            matchesDate = diffMs <= 30 * 24 * 60 * 60 * 1000;
          }
        }

        // Text Search
        const q = orderSearch.trim().toLowerCase();
        const matchesSearch =
          !q ||
          (order.orderNumber || '').toLowerCase().includes(q) ||
          (order.customerName || '').toLowerCase().includes(q) ||
          (order.userEmail || '').toLowerCase().includes(q) ||
          (order.phoneNumber || '').includes(q) ||
          (order.shippingAddress?.city || '').toLowerCase().includes(q) ||
          (order.courier || '').toLowerCase().includes(q) ||
          (order.trackingNumber || '').toLowerCase().includes(q);

        return matchesStatus && matchesDate && matchesSearch;
      })
      .sort((a, b) => {
        if (orderSortBy === 'newest') return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        if (orderSortBy === 'oldest') return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        if (orderSortBy === 'amount-high') return Number(b.totalAmount || 0) - Number(a.totalAmount || 0);
        if (orderSortBy === 'amount-low') return Number(a.totalAmount || 0) - Number(b.totalAmount || 0);
        return 0;
      });
  }, [orders, orderStatusFilter, orderDateRange, orderSearch, orderSortBy]);

  // Filter Catalog Products
  const filteredProducts = useMemo(() => {
    return products.filter((prod) => {
      const matchesCategory =
        productCategoryFilter === 'all' || prod.category === productCategoryFilter;

      const q = productSearch.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (prod.productName || '').toLowerCase().includes(q) ||
        (prod.material || '').toLowerCase().includes(q);

      return matchesCategory && matchesSearch;
    });
  }, [products, productCategoryFilter, productSearch]);

  // Product Add / Edit Modal Opener
  const openAddProductModal = () => {
    setEditingProduct(null);
    setProductFormData({
      productName: '',
      category: 'sofa',
      price: '',
      material: '',
      shortDesc: '',
      description: '',
      imgUrl: '',
      stockQuantity: 15,
      inStock: true,
    });
    setIsAddProductOpen(true);
  };

  const openEditProductModal = (piece) => {
    setEditingProduct(piece);
    const stockQty =
      piece.stockQuantity !== undefined
        ? piece.stockQuantity
        : piece.stock !== undefined
        ? piece.stock
        : 15;

    setProductFormData({
      productName: piece.productName || '',
      category: piece.category || 'sofa',
      price: piece.price || '',
      material: piece.material || '',
      shortDesc: piece.shortDesc || '',
      description: piece.description || '',
      imgUrl: piece.imgUrl || '',
      stockQuantity: stockQty,
      inStock: piece.inStock !== false && stockQty > 0,
    });
    setIsAddProductOpen(true);
  };

  // Convert uploaded image file to high-quality Base64 Data URL for instant storage
  const handleProductImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image file must be under 8MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setProductFormData((prev) => ({
        ...prev,
        imgUrl: dataUrl,
      }));
      toast.success('Image loaded! Click "Save to Catalog" to commit.');
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  // Save Product (Add or Edit) with Cloud Firestore Sync
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (!productFormData.productName.trim() || !productFormData.price) {
      toast.error('Please enter piece title and price');
      return;
    }

    const stockQty = Math.max(0, parseInt(productFormData.stockQuantity, 10) || 0);

    try {
      const payload = {
        ...productFormData,
        stockQuantity: stockQty,
        stock: stockQty,
        inStock: productFormData.inStock !== false && stockQty > 0,
        id: editingProduct?.id,
        createdAt: editingProduct?.createdAt,
      };

      const saved = await saveProduct(payload);

      setProducts((prev) => {
        const filtered = prev.filter((p) => p.id !== saved.id);
        return [saved, ...filtered];
      });

      toast.success(
        editingProduct
          ? `"${saved.productName}" updated in Atelier Catalog!`
          : `"${saved.productName}" added to Cloud Catalog!`
      );

      setIsAddProductOpen(false);
      setEditingProduct(null);
    } catch {
      toast.error('Failed to save product piece');
    }
  };

  // Quick Stock Toggle directly on Product Card
  const handleToggleStock = async (piece) => {
    const currentQty = piece.stockQuantity !== undefined ? piece.stockQuantity : piece.stock !== undefined ? piece.stock : 10;
    const isCurrentlyInStock = piece.inStock !== false && currentQty > 0;
    const updatedStatus = !isCurrentlyInStock;
    const newStockQty = updatedStatus ? (currentQty > 0 ? currentQty : 10) : 0;

    try {
      const updatedPiece = {
        ...piece,
        inStock: updatedStatus,
        stockQuantity: newStockQty,
        stock: newStockQty,
      };
      await saveProduct(updatedPiece);
      setProducts((prev) => prev.map((p) => (p.id === piece.id ? updatedPiece : p)));
      toast.info(
        `"${piece.productName}" is now ${updatedStatus ? `In Stock (${newStockQty} units)` : 'Out of Stock'}`
      );
    } catch {
      toast.error('Failed to update stock status');
    }
  };

  // Delete Piece
  const handleDeletePiece = async (id, name) => {
    if (window.confirm(`Delete "${name}" from atelier catalog?`)) {
      try {
        await deleteProduct(id);
        setProducts((prev) => prev.filter((p) => p.id !== id));
        toast.info(`"${name}" removed from catalog`);
      } catch {
        toast.error('Failed to delete piece');
      }
    }
  };

  // Save Storefront Settings
  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      const updated = await updateStoreSettings(storeSettings);
      setStoreSettings(updated);
      toast.success('Atelier Storefront settings saved and synchronized!');
    } catch {
      toast.error('Failed to save store settings');
    }
  };

  // Direct Admin Logout
  const handleAdminLogout = async () => {
    if (window.confirm('Sign out from Atelier Command Center?')) {
      try {
        await signOut(auth);
        toast.info('Signed out of Command Center');
        navigate('/login');
      } catch {
        toast.error('Sign out error');
      }
    }
  };

  // Client Lifetime Analytics (LTV & Order History Aggregation)
  const clientProfiles = useMemo(() => {
    return users.map((usr) => {
      const clientOrders = orders.filter(
        (o) =>
          (o.userEmail && o.userEmail.toLowerCase() === (usr.email || '').toLowerCase()) ||
          (o.customerName && o.customerName.toLowerCase() === (usr.displayName || '').toLowerCase())
      );

      const totalSpent = clientOrders.reduce((sum, o) => sum + Number(o.totalAmount || 0), 0);
      const ordersCount = clientOrders.length;
      const latestOrder = clientOrders[0]?.createdAt;

      let tier = 'Patron';
      if (totalSpent >= 2000 || usr.role === 'admin') tier = 'Royal Patron';
      else if (totalSpent >= 500) tier = 'Esteemed Patron';

      return {
        ...usr,
        totalSpent,
        ordersCount,
        latestOrder,
        tier,
      };
    });
  }, [users, orders]);

  // Handle toggling user role between Admin and Customer
  const handleToggleUserRole = async (usr) => {
    const isCurrentlyAdmin = usr.role === 'admin';
    const newRole = isCurrentlyAdmin ? 'customer' : 'admin';
    const promptMsg = isCurrentlyAdmin
      ? `Revoke Admin access for ${usr.displayName || usr.email}?`
      : `Promote ${usr.displayName || usr.email} to Atelier Administrator?`;

    if (!window.confirm(promptMsg)) return;

    try {
      await updateUserRole(usr.id, newRole);
      setUsers((prev) =>
        prev.map((u) => (u.id === usr.id ? { ...u, role: newRole } : u))
      );
      toast.success(`Role updated successfully to ${newRole}!`);
    } catch (err) {
      toast.error('Failed to update user role.');
    }
  };

  // Coupon Management Handlers
  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    if (!couponFormData.code.trim()) {
      toast.error('Please enter a voucher code');
      return;
    }
    try {
      const created = await createCoupon(couponFormData);
      setCoupons((prev) => [created, ...prev.filter((c) => c.code !== created.code)]);
      setCouponFormData({
        code: '',
        type: 'percentage',
        value: 15,
        minOrderAmount: 0,
        maxDiscount: 250,
        description: '',
      });
      toast.success(`VIP Voucher "${created.code}" created successfully!`);
    } catch (err) {
      toast.error(err.message || 'Failed to create voucher');
    }
  };

  const handleToggleCoupon = async (coupon) => {
    try {
      const updatedStatus = await toggleCouponStatus(coupon.id, coupon.active);
      setCoupons((prev) =>
        prev.map((c) => (c.id === coupon.id ? { ...c, active: updatedStatus } : c))
      );
      toast.info(`Voucher "${coupon.code}" is now ${updatedStatus ? 'ACTIVE' : 'PAUSED'}.`);
    } catch (err) {
      toast.error('Failed to toggle voucher status');
    }
  };

  const handleDeleteCoupon = async (coupon) => {
    if (!window.confirm(`Permanently remove VIP Voucher "${coupon.code}"?`)) return;
    try {
      await deleteCoupon(coupon.id);
      setCoupons((prev) => prev.filter((c) => c.id !== coupon.id));
      toast.success(`Voucher "${coupon.code}" removed.`);
    } catch (err) {
      toast.error('Failed to remove voucher');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recent';
    try {
      const d = new Date(dateString);
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getRelativeTime = (dateString) => {
    if (!dateString) return 'Just now';
    try {
      const diffMs = Date.now() - new Date(dateString).getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours < 1) return 'Just now';
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    } catch {
      return 'Recently';
    }
  };

  return (
    <Helmet title="Atelier Command Center | L'HABILLEUR Admin">
      <div className="admin__dashboard">
        <Container fluid="xl">
          {/* Executive Welcome Banner */}
          <div className="admin__welcome-banner">
            <div className="admin__banner-content">
              <div>
                <div className="admin__director-badge">
                  <i className="ri-vip-crown-fill"></i> Master Atelier Director
                </div>
                <h1 className="admin__director-title">
                  Command Center &bull; <span>Director {currentUser?.displayName || 'Administrator'}</span>
                </h1>
                <p className="admin__director-desc">
                  <span className="admin__status-online">
                    <span className="admin__live-dot"></span> Firestore Realtime Node Active
                  </span>
                  <span>&bull;</span>
                  <span>SYSTEM CLOCK: {currentTime}</span>
                  <span>&bull;</span>
                  <span>Store Status: <strong style={{ color: storeSettings.storeStatus === 'online' ? '#34d399' : '#fbbf24' }}>{storeSettings.storeStatus.toUpperCase()}</strong></span>
                </p>
              </div>

              {/* Quick Summary Pill Strip & Logout */}
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <div className="admin__banner-stats-pill">
                  <div className="admin__pill-item">
                    <span className="admin__pill-label">Average Commission</span>
                    <span className="admin__pill-val">${stats.avgOrderValue}</span>
                  </div>
                  <div className="admin__pill-item">
                    <span className="admin__pill-label">Fulfillment Rate</span>
                    <span className="admin__pill-val" style={{ color: '#34d399' }}>
                      {stats.deliveryRate}%
                    </span>
                  </div>
                  <div className="admin__pill-item">
                    <span className="admin__pill-label">Pending Dispatch</span>
                    <span className="admin__pill-val" style={{ color: '#fbbf24' }}>
                      {stats.pending}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  className={`admin__btn-invoice ${soundEnabled ? 'text-warning' : 'text-muted'}`}
                  onClick={() => {
                    const next = !soundEnabled;
                    setSoundEnabled(next);
                    if (next) playLuxuryChime();
                    toast.info(`Order Radar Chime is now ${next ? 'ENABLED' : 'MUTED'}`);
                  }}
                  title="Toggle audio chime alert on new incoming orders"
                  style={{ borderColor: soundEnabled ? 'rgba(212, 175, 55, 0.5)' : 'rgba(255, 255, 255, 0.15)' }}
                >
                  <i className={soundEnabled ? 'ri-volume-up-line' : 'ri-volume-mute-line'}></i>
                  Radar Chime: {soundEnabled ? 'ON' : 'MUTED'}
                </button>

                <button
                  type="button"
                  className="admin__btn-invoice"
                  onClick={handleAdminLogout}
                  title="Sign out from Command Center"
                >
                  <i className="ri-logout-box-r-line"></i> Exit
                </button>
              </div>
            </div>
          </div>

          {/* Navigation Tabs Bar */}
          <nav className="admin__tabs-bar" aria-label="Admin Navigation Tabs">
            <button
              type="button"
              className={`admin__tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <i className="ri-dashboard-3-line"></i> Overview & Analytics
            </button>

            <button
              type="button"
              className={`admin__tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              <i className="ri-shopping-cart-2-line"></i> Live Orders Matrix
              <span className="admin__tab-badge">{stats.orders}</span>
            </button>

            <button
              type="button"
              className={`admin__tab-btn ${activeTab === 'products' ? 'active' : ''}`}
              onClick={() => setActiveTab('products')}
            >
              <i className="ri-archive-line"></i> Atelier Catalog
              <span className="admin__tab-badge">{stats.products}</span>
            </button>

            <button
              type="button"
              className={`admin__tab-btn ${activeTab === 'clients' ? 'active' : ''}`}
              onClick={() => setActiveTab('clients')}
            >
              <i className="ri-user-star-line"></i> VIP Client Directory
              <span className="admin__tab-badge">{stats.clients}</span>
            </button>

            <button
              type="button"
              className={`admin__tab-btn ${activeTab === 'coupons' ? 'active' : ''}`}
              onClick={() => setActiveTab('coupons')}
            >
              <i className="ri-coupon-3-line"></i> VIP Vouchers & Promos
              <span className="admin__tab-badge">{coupons.filter(c => c.active).length}</span>
            </button>

            <button
              type="button"
              className={`admin__tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              <i className="ri-settings-4-line"></i> Atelier Settings
            </button>
          </nav>

          {/* TAB 1: OVERVIEW & ANALYTICS */}
          {activeTab === 'overview' && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* 4 Supercharged KPI Cards */}
              <Row className="g-4 mb-4">
                <Col lg="3" md="6">
                  <div className="admin__kpi-card-lux">
                    <div className="admin__kpi-header">
                      <div className="admin__kpi-icon-wrap gold">
                        <i className="ri-money-dollar-circle-line"></i>
                      </div>
                      <span className="admin__kpi-trend-pill">
                        <i className="ri-arrow-up-line"></i> +24.8% MoM
                      </span>
                    </div>
                    <div className="admin__kpi-label-lux">Gross Atelier Volume</div>
                    <div className="admin__kpi-value-lux">${stats.revenue.toLocaleString()}</div>
                    <div className="admin__kpi-subcaption">
                      <span>Avg. Ticket: ${stats.avgOrderValue}</span>
                      <span style={{ color: 'var(--admin-gold)', fontWeight: 'bold' }}>All Time</span>
                    </div>
                  </div>
                </Col>

                <Col lg="3" md="6">
                  <div className="admin__kpi-card-lux">
                    <div className="admin__kpi-header">
                      <div className="admin__kpi-icon-wrap blue">
                        <i className="ri-shopping-bag-line"></i>
                      </div>
                      <span
                        className="admin__kpi-trend-pill"
                        style={{
                          background: stats.pending > 0 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(56, 189, 248, 0.15)',
                          color: stats.pending > 0 ? '#fbbf24' : '#38bdf8',
                          borderColor: stats.pending > 0 ? 'rgba(245, 158, 11, 0.35)' : 'rgba(56, 189, 248, 0.35)',
                        }}
                      >
                        {stats.pending} Action Needed
                      </span>
                    </div>
                    <div className="admin__kpi-label-lux">Commissioned Orders</div>
                    <div className="admin__kpi-value-lux">{stats.orders}</div>
                    <div className="admin__kpi-subcaption">
                      <span>{stats.delivered} Delivered ({stats.deliveryRate}%)</span>
                      <span style={{ color: '#38bdf8' }}>Turnaround: 3d</span>
                    </div>
                  </div>
                </Col>

                <Col lg="3" md="6">
                  <div className="admin__kpi-card-lux">
                    <div className="admin__kpi-header">
                      <div className="admin__kpi-icon-wrap green">
                        <i className="ri-store-2-line"></i>
                      </div>
                      <span className="admin__kpi-trend-pill" style={{ color: '#34d399', background: 'rgba(16, 185, 129, 0.15)' }}>
                        <i className="ri-checkbox-circle-line"></i> Synchronized
                      </span>
                    </div>
                    <div className="admin__kpi-label-lux">Atelier Pieces In Catalog</div>
                    <div className="admin__kpi-value-lux">{stats.products}</div>
                    <div className="admin__kpi-subcaption">
                      <span>{products.filter((p) => p.inStock !== false).length} In Stock</span>
                      <span style={{ color: '#34d399' }}>Cloud Active</span>
                    </div>
                  </div>
                </Col>

                <Col lg="3" md="6">
                  <div className="admin__kpi-card-lux">
                    <div className="admin__kpi-header">
                      <div className="admin__kpi-icon-wrap purple">
                        <i className="ri-vip-crown-line"></i>
                      </div>
                      <span className="admin__kpi-trend-pill" style={{ color: '#c084fc', background: 'rgba(192, 132, 252, 0.15)' }}>
                        ★ 4.9 Patron Rating
                      </span>
                    </div>
                    <div className="admin__kpi-label-lux">VIP Patrons & Clients</div>
                    <div className="admin__kpi-value-lux">{stats.clients}</div>
                    <div className="admin__kpi-subcaption">
                      <span>High Retention</span>
                      <span style={{ color: '#c084fc' }}>Atelier Club</span>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Dynamic Interactive Performance Studio */}
              <Row className="g-4 mb-4">
                <Col lg="8">
                  <div className="admin__chart-panel">
                    <div className="admin__chart-header">
                      <div>
                        <h3 className="admin__chart-title">
                          <i className="ri-line-chart-line" style={{ color: 'var(--admin-gold)' }}></i>
                          Atelier Revenue Velocity
                        </h3>
                        <div className="admin__chart-subtitle">
                          Dynamic Transaction Curve Computed from Real Client Orders
                        </div>
                      </div>

                      <div className="admin__chart-timeframe-btns">
                        <button
                          type="button"
                          className={`admin__timeframe-btn ${chartTimeframe === '7d' ? 'active' : ''}`}
                          onClick={() => setChartTimeframe('7d')}
                        >
                          7 Days
                        </button>
                        <button
                          type="button"
                          className={`admin__timeframe-btn ${chartTimeframe === '30d' ? 'active' : ''}`}
                          onClick={() => setChartTimeframe('30d')}
                        >
                          30 Days
                        </button>
                        <button
                          type="button"
                          className={`admin__timeframe-btn ${chartTimeframe === '1y' ? 'active' : ''}`}
                          onClick={() => setChartTimeframe('1y')}
                        >
                          1 Year
                        </button>
                      </div>
                    </div>

                    <div className="admin__chart-wrapper">
                      <svg className="admin__chart-svg" viewBox="0 0 700 200">
                        <defs>
                          <linearGradient id="adminGoldGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#d4af37" stopOpacity="0.55" />
                            <stop offset="70%" stopColor="#d4af37" stopOpacity="0.08" />
                            <stop offset="100%" stopColor="#d4af37" stopOpacity="0.0" />
                          </linearGradient>
                        </defs>

                        {/* Dashed Grid Lines */}
                        <line x1="30" y1="30" x2="670" y2="30" className="admin__chart-grid-line" />
                        <line x1="30" y1="80" x2="670" y2="80" className="admin__chart-grid-line" />
                        <line x1="30" y1="130" x2="670" y2="130" className="admin__chart-grid-line" />

                        {/* Dynamic Smooth Area Fill */}
                        <path d={chartData.areaPath} fill="url(#adminGoldGrad)" />

                        {/* Dynamic Smooth Main Line */}
                        <path d={chartData.linePath} className="admin__chart-line" />

                        {/* Dynamic Interactive Nodes */}
                        {chartData.points.map((pt, idx) => (
                          <circle
                            key={idx}
                            cx={pt.x}
                            cy={pt.y}
                            className="admin__chart-point"
                            title={`Volume: $${pt.val.toLocaleString()}`}
                          />
                        ))}
                      </svg>

                      <div className="admin__chart-labels">
                        {chartData.labels.map((lbl, idx) => (
                          <span key={idx}>{lbl}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </Col>

                {/* Right Column: Dynamic Category Performance & Revenue Target */}
                <Col lg="4">
                  <div className="admin__chart-panel h-100 d-flex flex-column justify-content-between">
                    <div>
                      <div className="admin__chart-header mb-3 pb-2">
                        <h3 className="admin__chart-title" style={{ fontSize: '1.1rem' }}>
                          <i className="ri-pie-chart-line" style={{ color: '#38bdf8' }}></i>
                          Category Performance
                        </h3>
                      </div>

                      <div className="d-flex flex-column gap-3">
                        {categoryStats.map((cat) => (
                          <div key={cat.key}>
                            <div className="d-flex justify-content-between small mb-1">
                              <span className="fw-semibold text-white">{cat.name}</span>
                              <span style={{ color: 'var(--admin-gold)', fontWeight: '700' }}>
                                {cat.percent}% &bull; ${cat.revenue.toLocaleString()}
                              </span>
                            </div>
                            <div className="admin__category-bar-track">
                              <div
                                className="admin__category-bar-fill"
                                style={{ width: `${cat.percent}%`, background: cat.color }}
                              ></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-3 rounded mt-3" style={{ background: 'rgba(212, 175, 55, 0.08)', border: '1px solid rgba(212, 175, 55, 0.25)' }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="small text-muted">Monthly Revenue Target</span>
                        <strong className="small" style={{ color: 'var(--admin-gold)' }}>
                          ${stats.currentMonthRevenue.toLocaleString()} / ${stats.monthlyGoal.toLocaleString()}
                        </strong>
                      </div>
                      <div className="admin__category-bar-track mt-2">
                        <div
                          className="admin__category-bar-fill"
                          style={{
                            width: `${stats.monthlyProgress}%`,
                            background: 'linear-gradient(90deg, var(--admin-gold), #facc15)',
                          }}
                        ></div>
                      </div>
                      <div className="text-end small mt-1" style={{ color: '#34d399', fontSize: '0.75rem', fontWeight: 'bold' }}>
                        {stats.monthlyProgress}% Achieved This Month
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Recent Commissions Activity Feed */}
              <div className="admin__chart-panel">
                <div className="admin__chart-header">
                  <h3 className="admin__chart-title">
                    <i className="ri-history-line" style={{ color: 'var(--admin-gold)' }}></i>
                    Recent Commissions Activity Feed
                  </h3>
                  <button
                    type="button"
                    className="admin__btn-invoice"
                    onClick={() => setActiveTab('orders')}
                  >
                    Manage All Live Orders ({stats.orders}) &rarr;
                  </button>
                </div>

                {orders.length === 0 ? (
                  <div className="text-center py-5 text-muted">No orders received yet.</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {orders.slice(0, 4).map((ord) => (
                      <div
                        key={ord.id}
                        className="d-flex align-items-center justify-content-between p-3 rounded"
                        style={{
                          background: 'rgba(255,255,255,0.02)',
                          border: '1px solid var(--admin-border-subtle)',
                        }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div className="admin__client-avatar">
                            {(ord.customerName || 'P').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <strong className="text-white d-block">{ord.customerName}</strong>
                            <span style={{ color: '#cbd5e1', fontSize: '0.82rem' }}>
                              #{ord.orderNumber} &bull; {getRelativeTime(ord.createdAt)}
                            </span>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-4">
                          <div className="text-end">
                            <span className="text-white fw-bold d-block">${ord.totalAmount}</span>
                            <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                              {ord.items?.length || 0} pcs
                            </span>
                          </div>

                          <span className={`admin__status-badge ${(ord.status || 'pending').toLowerCase()}`}>
                            {ord.status || 'Pending'}
                          </span>

                          <button
                            type="button"
                            className="admin__btn-invoice"
                            onClick={() => setSelectedInvoiceOrder(ord)}
                          >
                            <i className="ri-file-text-line"></i> Invoice
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* TAB 2: LIVE ORDERS MATRIX */}
          {activeTab === 'orders' && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Order Status Pipeline Filter Ribbon */}
              <div className="admin__pipeline-ribbon">
                <div
                  className={`admin__pipeline-chip ${orderStatusFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setOrderStatusFilter('all')}
                >
                  <div>
                    <div className="admin__pipeline-chip-title">All Commissions</div>
                    <div className="admin__pipeline-chip-count">{stats.orders}</div>
                  </div>
                  <i className="ri-stack-line fs-3 text-muted"></i>
                </div>

                <div
                  className={`admin__pipeline-chip ${orderStatusFilter === 'pending' ? 'active' : ''}`}
                  onClick={() => setOrderStatusFilter('pending')}
                  style={{ borderColor: stats.pending > 0 ? 'rgba(245, 158, 11, 0.4)' : '' }}
                >
                  <div>
                    <div className="admin__pipeline-chip-title" style={{ color: '#fbbf24' }}>
                      Pending Action
                    </div>
                    <div className="admin__pipeline-chip-count" style={{ color: '#fbbf24' }}>
                      {stats.pending}
                    </div>
                  </div>
                  <i className="ri-hourglass-2-line fs-3 text-warning"></i>
                </div>

                <div
                  className={`admin__pipeline-chip ${orderStatusFilter === 'processing' ? 'active' : ''}`}
                  onClick={() => setOrderStatusFilter('processing')}
                >
                  <div>
                    <div className="admin__pipeline-chip-title" style={{ color: '#38bdf8' }}>
                      In Workshop
                    </div>
                    <div className="admin__pipeline-chip-count" style={{ color: '#38bdf8' }}>
                      {stats.processing}
                    </div>
                  </div>
                  <i className="ri-tools-line fs-3" style={{ color: '#38bdf8' }}></i>
                </div>

                <div
                  className={`admin__pipeline-chip ${orderStatusFilter === 'shipped' ? 'active' : ''}`}
                  onClick={() => setOrderStatusFilter('shipped')}
                >
                  <div>
                    <div className="admin__pipeline-chip-title" style={{ color: '#c084fc' }}>
                      On Transit
                    </div>
                    <div className="admin__pipeline-chip-count" style={{ color: '#c084fc' }}>
                      {stats.shipped}
                    </div>
                  </div>
                  <i className="ri-truck-line fs-3" style={{ color: '#c084fc' }}></i>
                </div>

                <div
                  className={`admin__pipeline-chip ${orderStatusFilter === 'delivered' ? 'active' : ''}`}
                  onClick={() => setOrderStatusFilter('delivered')}
                >
                  <div>
                    <div className="admin__pipeline-chip-title" style={{ color: '#34d399' }}>
                      Delivered
                    </div>
                    <div className="admin__pipeline-chip-count" style={{ color: '#34d399' }}>
                      {stats.delivered}
                    </div>
                  </div>
                  <i className="ri-checkbox-circle-line fs-3 text-success"></i>
                </div>
              </div>

              {/* Action Toolbar with Date Filters and Bulk Ops */}
              <div className="admin__chart-panel p-3 mb-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div className="d-flex align-items-center gap-3 flex-wrap flex-grow-1">
                    {/* Search Input */}
                    <div className="admin__search-box" style={{ width: '320px' }}>
                      <i className="ri-search-line"></i>
                      <input
                        type="text"
                        className="admin__search-input"
                        placeholder="Search Ref, Client, Courier, Tracking..."
                        value={orderSearch}
                        onChange={(e) => setOrderSearch(e.target.value)}
                      />
                      {orderSearch && (
                        <button
                          type="button"
                          className="btn btn-link text-muted p-0"
                          onClick={() => setOrderSearch('')}
                        >
                          <i className="ri-close-circle-line"></i>
                        </button>
                      )}
                    </div>

                    {/* Date Range Chips */}
                    <div className="admin__filter-group">
                      {[
                        { id: 'all', label: 'All Dates' },
                        { id: 'today', label: 'Today' },
                        { id: '7d', label: 'Last 7D' },
                        { id: 'month', label: 'This Month' },
                      ].map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className={`admin__filter-pill ${orderDateRange === item.id ? 'active' : ''}`}
                          onClick={() => setOrderDateRange(item.id)}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>

                    {/* Sort Dropdown */}
                    <div className="d-flex align-items-center gap-2">
                      <span className="small" style={{ color: '#cbd5e1', fontWeight: '600' }}>
                        Sort:
                      </span>
                      <select
                        className="admin__sort-select"
                        value={orderSortBy}
                        onChange={(e) => setOrderSortBy(e.target.value)}
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="amount-high">Highest Total</option>
                        <option value="amount-low">Lowest Total</option>
                      </select>
                    </div>
                  </div>

                  {/* Right Actions */}
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="admin__btn-invoice"
                      onClick={() => handleExportCSV(false)}
                      title="Download Orders as CSV"
                    >
                      <i className="ri-download-2-line"></i> Export Ledger (.CSV)
                    </button>

                    <button
                      type="button"
                      className="admin__refresh-btn"
                      onClick={loadDashboardData}
                      title="Refresh Orders"
                    >
                      <i className={`ri-refresh-line ${refreshing ? 'ri-spin' : ''}`}></i>
                    </button>
                  </div>
                </div>

                {/* Batch Action Strip (shows when 1+ orders selected) */}
                {selectedOrderIds.length > 0 && (
                  <div
                    className="d-flex align-items-center justify-content-between p-2 mt-3 rounded"
                    style={{
                      background: 'rgba(212, 175, 55, 0.12)',
                      border: '1px solid var(--admin-gold)',
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge bg-warning text-dark fw-bold">
                        {selectedOrderIds.length} Selected
                      </span>
                      <span className="small text-white">Batch Operations:</span>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-info py-1 px-2"
                        onClick={() => handleBulkStatusChange('Processing')}
                      >
                        Mark Workshop
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary py-1 px-2"
                        style={{ color: '#c084fc', borderColor: '#c084fc' }}
                        onClick={() => handleBulkStatusChange('Shipped')}
                      >
                        Mark Shipped
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-success py-1 px-2"
                        onClick={() => handleBulkStatusChange('Delivered')}
                      >
                        Mark Delivered
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-light py-1 px-2"
                        onClick={() => handleExportCSV(true)}
                      >
                        Export Selected
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-muted py-1 px-2"
                        onClick={() => setSelectedOrderIds([])}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Orders List Container */}
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-warning" role="status"></div>
                  <p className="mt-3 text-muted">Syncing live commissions from Cloud Firestore...</p>
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="admin__chart-panel text-center py-5">
                  <i className="ri-inbox-line fs-1 text-muted mb-2 d-block"></i>
                  <h4 className="text-white">No Orders Found</h4>
                  <p className="text-muted">No client commissions match your current filter criteria.</p>
                  <button
                    type="button"
                    className="admin__btn-invoice mt-2"
                    onClick={() => {
                      setOrderStatusFilter('all');
                      setOrderDateRange('all');
                      setOrderSearch('');
                    }}
                  >
                    Reset Filter
                  </button>
                </div>
              ) : (
                <div className="admin__orders-stream-wrapper">
                  <div className="admin__orders-table-header">
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="checkbox"
                        checked={
                          selectedOrderIds.length > 0 &&
                          selectedOrderIds.length === filteredOrders.length
                        }
                        onChange={handleSelectAllOrders}
                        style={{ cursor: 'pointer', accentColor: 'var(--admin-gold)' }}
                        title="Select All Orders"
                      />
                      <span>Ref & Date</span>
                    </div>
                    <div>VIP Client Profile</div>
                    <div>Delivery Destination</div>
                    <div>Pieces Included</div>
                    <div>Commission & Status</div>
                    <div className="text-end">Actions</div>
                  </div>

                  <div className="d-flex flex-column gap-3">
                    {filteredOrders.map((ord) => {
                      const isExpanded = expandedOrderId === ord.id;
                      const isSelected = selectedOrderIds.includes(ord.id);
                      const statusKey = (ord.status || 'pending').toLowerCase();

                      return (
                        <div
                          key={ord.id}
                          className="admin__order-card-lux"
                          style={{
                            borderColor: isSelected ? 'var(--admin-gold)' : undefined,
                            background: isSelected ? 'rgba(212, 175, 55, 0.05)' : undefined,
                          }}
                        >
                          <div className="admin__order-main-row">
                            {/* Col 1: Order Meta & Checkbox */}
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelectOrder(ord.id)}
                                style={{ cursor: 'pointer', accentColor: 'var(--admin-gold)' }}
                              />
                              <div>
                                <span className="admin__order-num-pill">#{ord.orderNumber}</span>
                                <div className="text-white small fw-bold">{formatDate(ord.createdAt)}</div>
                                <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                                  {getRelativeTime(ord.createdAt)}
                                </span>
                              </div>
                            </div>

                            {/* Col 2: Customer Profile */}
                            <div className="admin__client-flex">
                              <div className="admin__client-avatar">
                                {(ord.customerName || 'C').charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className="admin__client-name text-truncate">
                                  {ord.customerName}
                                </div>
                                <div className="admin__client-contact text-truncate">
                                  <i className="ri-mail-line"></i> {ord.userEmail}
                                </div>
                                <div className="admin__client-contact">
                                  <i className="ri-phone-line"></i>
                                  <a href={`tel:${ord.phoneNumber}`} className="admin__client-phone">
                                    {ord.phoneNumber}
                                  </a>
                                </div>
                              </div>
                            </div>

                            {/* Col 3: Shipping Location & Courier Details */}
                            <div>
                              <div className="d-flex align-items-center gap-1 text-white small fw-bold">
                                <i className="ri-map-pin-line text-warning"></i>
                                <span>
                                  {ord.shippingAddress?.city}, {ord.shippingAddress?.country || 'Egypt'}
                                </span>
                              </div>
                              <div
                                className="text-truncate"
                                style={{ maxWidth: '200px', color: '#cbd5e1', fontSize: '0.82rem' }}
                              >
                                {ord.shippingAddress?.address}
                              </div>
                              <div className="d-flex align-items-center gap-2 flex-wrap mt-1">
                                <span className="admin__payment-badge">
                                  {ord.paymentMethod || 'Cash on Delivery'}
                                </span>
                                {ord.courier && (
                                  <span
                                    className="badge"
                                    style={{
                                      background: 'rgba(192, 132, 252, 0.15)',
                                      color: '#c084fc',
                                      border: '1px solid rgba(192, 132, 252, 0.3)',
                                      fontSize: '0.7rem',
                                    }}
                                  >
                                    <i className="ri-truck-line me-1"></i>
                                    {ord.courier} {ord.trackingNumber ? `#${ord.trackingNumber}` : ''}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Col 4: Thumbnails Stack */}
                            <div>
                              <div className="admin__thumbs-overlap">
                                {ord.items?.slice(0, 3).map((it, idx) => (
                                  <img
                                    key={idx}
                                    src={getProductImg(it)}
                                    onError={(e) => handleImgError(e, it)}
                                    alt={it.productName}
                                    className="admin__thumb-stack-item"
                                    title={`${it.productName} (${it.quantity}x)`}
                                  />
                                ))}
                                {ord.items?.length > 3 && (
                                  <div className="admin__thumb-more-badge">
                                    +{ord.items.length - 3}
                                  </div>
                                )}
                              </div>
                              <span style={{ color: '#94a3b8', fontSize: '0.78rem' }} className="d-block mt-1">
                                {ord.items?.length || 0} Piece{(ord.items?.length || 0) === 1 ? '' : 's'}
                              </span>
                            </div>

                            {/* Col 5: Total & Realtime Status Changer */}
                            <div>
                              <div className="admin__price-bold">${ord.totalAmount}</div>
                              <select
                                className={`admin__status-dropdown-lux ${statusKey} mt-1`}
                                value={statusKey}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const capitalized = val.charAt(0).toUpperCase() + val.slice(1);
                                  handleStatusSelectChange(ord.id, capitalized);
                                }}
                              >
                                <option value="pending">🟡 Pending</option>
                                <option value="processing">🔵 In Workshop</option>
                                <option value="shipped">🟣 Shipped</option>
                                <option value="delivered">🟢 Delivered</option>
                                <option value="cancelled">🔴 Cancelled</option>
                              </select>
                            </div>

                            {/* Col 6: Actions */}
                            <div className="d-flex align-items-center justify-content-end gap-2">
                              <button
                                type="button"
                                className="admin__btn-invoice"
                                onClick={() => setSelectedInvoiceOrder(ord)}
                                title="Print / Save Official Invoice"
                              >
                                <i className="ri-printer-line"></i> Invoice
                              </button>

                              <button
                                type="button"
                                className="admin__btn-details"
                                onClick={() => setExpandedOrderId(isExpanded ? null : ord.id)}
                                title="Toggle Itemized Breakdown"
                              >
                                <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line`}></i>
                              </button>

                              <button
                                type="button"
                                className="admin__btn-delete-order"
                                onClick={() => handleDeleteOrder(ord.id, ord.orderNumber)}
                                title="Archive Record"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          </div>

                          {/* Expandable Inline Details Box */}
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                className="admin__order-expanded-box"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.22 }}
                              >
                                <div style={{ gridColumn: '1 / -1', width: '100%' }}>
                                  <OrderTracker
                                    status={ord.status}
                                    trackingInfo={{
                                      courierName: ord.courier,
                                      trackingNumber: ord.trackingNumber,
                                    }}
                                    createdAt={ord.createdAt}
                                  />
                                </div>

                                <div>
                                  <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h6 className="text-warning small text-uppercase fw-bold mb-0">
                                      <i className="ri-box-3-line me-1"></i> Commissioned Pieces Details ({ord.items?.length || 0})
                                    </h6>
                                    {ord.couponCode && (
                                      <span className="badge" style={{ background: 'rgba(212, 175, 55, 0.2)', color: '#d4af37', border: '1px solid rgba(212, 175, 55, 0.4)' }}>
                                        <i className="ri-coupon-3-line me-1"></i> Voucher: {ord.couponCode} (-${ord.discountAmount || 0})
                                      </span>
                                    )}
                                  </div>
                                  {ord.items?.map((it, idx) => (
                                    <div key={idx} className="admin__expanded-item-row">
                                      <img
                                        src={getProductImg(it)}
                                        onError={(e) => handleImgError(e, it)}
                                        alt={it.productName}
                                        className="admin__expanded-item-thumb"
                                      />
                                      <div className="flex-grow-1">
                                        <strong className="text-white d-block">{it.productName}</strong>
                                        <span style={{ color: '#cbd5e1', fontSize: '0.82rem' }}>
                                          Unit Price: ${it.price} &bull; Qty: {it.quantity}
                                        </span>
                                      </div>
                                      <strong className="text-warning">
                                        ${it.totalPrice || it.price * it.quantity}
                                      </strong>
                                    </div>
                                  ))}
                                </div>

                                <div>
                                  <h6 className="text-warning small text-uppercase fw-bold mb-3">
                                    <i className="ri-map-pin-user-line me-1"></i> Logistics & Courier Tracking
                                  </h6>
                                  <div
                                    className="p-3 rounded"
                                    style={{
                                      background: 'rgba(255,255,255,0.04)',
                                      border: '1px solid var(--admin-border-subtle)',
                                    }}
                                  >
                                    <div className="mb-2">
                                      <span style={{ color: '#94a3b8', fontSize: '0.78rem' }} className="d-block">
                                        Recipient:
                                      </span>
                                      <strong className="text-white">{ord.customerName}</strong>
                                    </div>
                                    <div className="mb-2">
                                      <span style={{ color: '#94a3b8', fontSize: '0.78rem' }} className="d-block">
                                        Delivery Address:
                                      </span>
                                      <span className="text-white">
                                        {ord.shippingAddress?.address}, {ord.shippingAddress?.city},{' '}
                                        {ord.shippingAddress?.postalCode}
                                      </span>
                                    </div>
                                    <div className="mb-2">
                                      <span style={{ color: '#94a3b8', fontSize: '0.78rem' }} className="d-block">
                                        Courier Partner:
                                      </span>
                                      <span className="text-white">
                                        {ord.courier || 'Atelier White-Glove Fleet'}
                                      </span>
                                    </div>
                                    {ord.trackingNumber && (
                                      <div className="mb-2">
                                        <span style={{ color: '#94a3b8', fontSize: '0.78rem' }} className="d-block">
                                          Tracking Reference:
                                        </span>
                                        <span className="text-warning font-monospace">
                                          {ord.trackingNumber}
                                        </span>
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-warning mt-2 w-100"
                                      onClick={() => {
                                        setCourierDispatchOrder(ord);
                                        setCourierName(ord.courier || 'Atelier White-Glove');
                                        setTrackingNumber(ord.trackingNumber || '');
                                      }}
                                    >
                                      <i className="ri-truck-line me-1"></i> Edit Courier Dispatch Info
                                    </button>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* TAB 3: ATELIER CATALOG (WITH CLOUD SYNC & EDIT MODAL) */}
          {activeTab === 'products' && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="admin__chart-panel">
                <div className="admin__toolbar">
                  <div className="admin__search-box">
                    <i className="ri-search-line"></i>
                    <input
                      type="text"
                      className="admin__search-input"
                      placeholder="Search pieces by title or material..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                    />
                  </div>

                  <div className="admin__filter-group">
                    {['all', 'sofa', 'chair', 'table', 'lighting', 'decor'].map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        className={`admin__filter-pill ${productCategoryFilter === cat ? 'active' : ''}`}
                        onClick={() => setProductCategoryFilter(cat)}
                      >
                        {cat.toUpperCase()}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="admin__action-btn"
                    onClick={openAddProductModal}
                  >
                    <i className="ri-add-line"></i> Add New Piece
                  </button>
                </div>

                <Row className="g-3">
                  {filteredProducts.map((piece) => (
                    <Col lg="4" md="6" key={piece.id}>
                      <div className="admin__product-card">
                        <div style={{ position: 'relative' }}>
                          <img
                            src={getProductImg(piece)}
                            onError={(e) => handleImgError(e, piece)}
                            alt={piece.productName}
                            className="admin__product-thumb"
                          />
                          <button
                            type="button"
                            onClick={() => handleToggleStock(piece)}
                            className="badge position-absolute top-0 end-0 m-3 border-0"
                            style={{
                              background:
                                piece.inStock !== false && (piece.stockQuantity ?? piece.stock ?? 0) > 0
                                  ? (piece.stockQuantity ?? piece.stock ?? 0) <= 5
                                    ? 'rgba(245, 158, 11, 0.95)'
                                    : 'rgba(16, 185, 129, 0.95)'
                                  : 'rgba(239, 68, 68, 0.95)',
                              color: '#ffffff',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              fontSize: '0.72rem',
                              padding: '5px 10px',
                              borderRadius: '8px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                            }}
                            title="Click to toggle availability"
                          >
                            {piece.inStock !== false && (piece.stockQuantity ?? piece.stock ?? 0) > 0
                              ? `In Stock (${piece.stockQuantity ?? piece.stock ?? 12} units)`
                              : 'Out of Stock (0)'}
                          </button>
                        </div>

                        <div className="admin__product-info">
                          <span className="admin__product-cat">{piece.category}</span>
                          <h4 className="admin__product-name">{piece.productName}</h4>
                          {piece.material && (
                            <span style={{ color: '#94a3b8', fontSize: '0.78rem' }} className="d-block mb-2">
                              Material: {piece.material}
                            </span>
                          )}

                          <div className="d-flex justify-content-between align-items-center mt-auto pt-2 border-top border-secondary">
                            <span className="admin__product-price">${piece.price}</span>
                            <div className="d-flex align-items-center gap-2">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-warning py-1 px-2"
                                onClick={() => openEditProductModal(piece)}
                                title="Edit Piece Specifications"
                              >
                                <i className="ri-edit-line"></i> Edit
                              </button>

                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger py-1 px-2"
                                onClick={() => handleDeletePiece(piece.id, piece.productName)}
                                title="Remove Piece"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </motion.div>
          )}

          {/* TAB 4: VIP CLIENT DIRECTORY (WITH LTV & SPEND ANALYTICS) */}
          {activeTab === 'clients' && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="admin__chart-panel">
                <div className="admin__chart-header">
                  <div>
                    <h3 className="admin__chart-title">
                      <i className="ri-user-star-line" style={{ color: 'var(--admin-gold)' }}></i>
                      Registered VIP Patrons & Clients
                    </h3>
                    <div className="admin__chart-subtitle">
                      Lifetime Value (LTV) and Direct Commissions Ledger
                    </div>
                  </div>
                  <span className="text-muted small">Synchronized with Cloud Firestore</span>
                </div>

                {clientProfiles.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    No registered client accounts in Cloud Firestore yet.
                  </div>
                ) : (
                  <div className="admin__table-container">
                    <table className="admin__table">
                      <thead>
                        <tr>
                          <th>Client Profile</th>
                          <th>Email Address</th>
                          <th>Privilege Tier</th>
                          <th>Access Role</th>
                          <th>Total Orders</th>
                          <th>Lifetime Value (LTV)</th>
                          <th>Registered Date</th>
                          <th>Manage Access</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientProfiles.map((usr) => {
                          const isUserAdmin = usr.role === 'admin';
                          // Admins cannot change their own role (also enforced in firestore.rules).
                          const isSelf = usr.id === currentUser?.uid;

                          return (
                            <tr key={usr.id}>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <div className="admin__client-avatar">
                                    {(usr.displayName || usr.email || 'U').charAt(0).toUpperCase()}
                                  </div>
                                  <strong className="text-white">
                                    {usr.displayName || 'Atelier Patron'}
                                  </strong>
                                </div>
                              </td>
                              <td style={{ color: '#cbd5e1' }}>{usr.email}</td>
                              <td>
                                <span
                                  className="badge"
                                  style={{
                                    background:
                                      usr.tier === 'Royal Patron'
                                        ? 'rgba(212, 175, 55, 0.2)'
                                        : usr.tier === 'Esteemed Patron'
                                        ? 'rgba(56, 189, 248, 0.2)'
                                        : 'rgba(255, 255, 255, 0.08)',
                                    color:
                                      usr.tier === 'Royal Patron'
                                        ? 'var(--admin-gold)'
                                        : usr.tier === 'Esteemed Patron'
                                        ? '#38bdf8'
                                        : '#cbd5e1',
                                    border:
                                      usr.tier === 'Royal Patron'
                                        ? '1px solid var(--admin-gold)'
                                        : '1px solid rgba(255,255,255,0.2)',
                                    padding: '5px 10px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                  }}
                                >
                                  {usr.tier}
                                </span>
                              </td>
                              <td>
                                <span
                                  className="badge"
                                  style={{
                                    background: isUserAdmin ? 'rgba(212, 175, 55, 0.18)' : 'rgba(255, 255, 255, 0.06)',
                                    color: isUserAdmin ? '#fbbf24' : '#94a3b8',
                                    border: isUserAdmin ? '1px solid #fbbf24' : '1px solid rgba(255, 255, 255, 0.15)',
                                    padding: '5px 10px',
                                  }}
                                >
                                  <i className={isUserAdmin ? "ri-shield-check-fill me-1" : "ri-user-line me-1"}></i>
                                  {isUserAdmin ? 'Administrator' : 'Customer'}
                                </span>
                              </td>
                              <td style={{ color: '#ffffff', fontWeight: 'bold' }}>
                                {usr.ordersCount} Orders
                              </td>
                              <td>
                                <strong style={{ color: 'var(--admin-gold)', fontSize: '1rem' }}>
                                  ${usr.totalSpent.toLocaleString()}
                                </strong>
                              </td>
                              <td>{formatDate(usr.createdAt)}</td>
                              <td>
                                {!isSelf && (
                                  <button
                                    type="button"
                                    className={`btn btn-sm ${isUserAdmin ? 'btn-outline-danger' : 'btn-outline-warning'}`}
                                    style={{ fontSize: '0.78rem', padding: '3px 10px', borderRadius: '6px' }}
                                    onClick={() => handleToggleUserRole(usr)}
                                    title={isUserAdmin ? 'Revoke admin credentials' : 'Grant admin credentials'}
                                  >
                                    {isUserAdmin ? 'Demote to Client' : 'Make Administrator'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* TAB 5: ATELIER LIVE STORE CONTROLS & SETTINGS */}
          {activeTab === 'settings' && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Row className="g-4">
                <Col lg="6">
                  <div className="admin__chart-panel h-100">
                    <div className="admin__chart-header">
                      <h3 className="admin__chart-title">
                        <i className="ri-shield-user-line" style={{ color: 'var(--admin-gold)' }}></i>
                        Master Administrator Profile
                      </h3>
                    </div>

                    <div className="d-flex align-items-center gap-3 mb-4">
                      <div
                        style={{
                          width: '68px',
                          height: '68px',
                          borderRadius: '16px',
                          background: 'linear-gradient(135deg, var(--admin-gold), #8a6d1a)',
                          color: '#070e1b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.9rem',
                          fontWeight: 'bold',
                        }}
                      >
                        {(currentUser?.displayName || currentUser?.email || 'A').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="m-0 text-white fw-bold">{currentUser?.displayName || 'Administrator'}</h4>
                        <span className="text-warning small font-monospace">{currentUser?.email}</span>
                        <div className="badge bg-success mt-1 d-block" style={{ width: 'fit-content' }}>
                          Administrator
                        </div>
                      </div>
                    </div>

                    <div className="border-top border-secondary pt-3 text-muted small">
                      <p className="mb-1" style={{ color: '#cbd5e1' }}>
                        <strong className="text-white">Security Protocol:</strong> Firebase Role-Based Access Control (RBAC).
                      </p>
                      <p className="mb-0" style={{ color: '#cbd5e1' }}>
                        <strong className="text-white">Privileges:</strong> Full control over orders, clients ledger, and catalog items.
                      </p>
                    </div>
                  </div>
                </Col>

                <Col lg="6">
                  <div className="admin__chart-panel h-100">
                    <div className="admin__chart-header">
                      <h3 className="admin__chart-title">
                        <i className="ri-equalizer-line" style={{ color: '#38bdf8' }}></i>
                        Storefront Live Configuration & Controls
                      </h3>
                    </div>

                    <form onSubmit={handleSettingsSubmit} className="d-flex flex-column gap-3">
                      <div className="d-flex justify-content-between align-items-center p-3 rounded" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--admin-border-subtle)' }}>
                        <div>
                          <strong className="text-white d-block">Storefront Public Status</strong>
                          <div style={{ color: '#cbd5e1', fontSize: '0.84rem' }}>
                            Toggle between Public Active and Scheduled Maintenance
                          </div>
                        </div>
                        <select
                          className="admin__sort-select"
                          value={storeSettings.storeStatus}
                          onChange={(e) =>
                            setStoreSettings({ ...storeSettings, storeStatus: e.target.value })
                          }
                        >
                          <option value="online">🟢 ONLINE</option>
                          <option value="maintenance">🟡 MAINTENANCE</option>
                        </select>
                      </div>

                      <div className="d-flex justify-content-between align-items-center p-3 rounded" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--admin-border-subtle)' }}>
                        <div>
                          <strong className="text-white d-block">White-Glove Delivery Fleet</strong>
                          <div style={{ color: '#cbd5e1', fontSize: '0.84rem' }}>
                            Dispatch state for insured transport across Egypt
                          </div>
                        </div>
                        <select
                          className="admin__sort-select"
                          value={storeSettings.whiteGloveDelivery}
                          onChange={(e) =>
                            setStoreSettings({ ...storeSettings, whiteGloveDelivery: e.target.value })
                          }
                        >
                          <option value="active">🟢 ACTIVE</option>
                          <option value="paused">🔴 PAUSED</option>
                        </select>
                      </div>

                      <div className="p-3 rounded" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--admin-border-subtle)' }}>
                        <label className="admin__form-label">Store Announcement Banner Text</label>
                        <input
                          type="text"
                          className="admin__form-input"
                          value={storeSettings.announcementText}
                          onChange={(e) =>
                            setStoreSettings({ ...storeSettings, announcementText: e.target.value })
                          }
                        />
                      </div>

                      <div className="d-flex justify-content-end">
                        <button type="submit" className="admin__action-btn">
                          <i className="ri-save-line"></i> Save Atelier Configuration
                        </button>
                      </div>
                    </form>
                  </div>
                </Col>
              </Row>
            </motion.div>
          )}

          {/* TAB 6: VIP PROMOTIONAL VOUCHERS & COUPON ENGINE */}
          {activeTab === 'coupons' && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Promo Engine Overview KPIs */}
              <Row className="g-3 mb-4">
                <Col lg="3" sm="6">
                  <div className="admin__kpi-card">
                    <div className="admin__kpi-icon gold">
                      <i className="ri-coupon-3-line"></i>
                    </div>
                    <div className="admin__kpi-info">
                      <span className="admin__kpi-label">Total Vouchers</span>
                      <h3 className="admin__kpi-value">{coupons.length}</h3>
                      <span className="admin__kpi-trend positive">
                        <i className="ri-shield-check-line"></i> Catalog Wide
                      </span>
                    </div>
                  </div>
                </Col>

                <Col lg="3" sm="6">
                  <div className="admin__kpi-card">
                    <div className="admin__kpi-icon green">
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                    <div className="admin__kpi-info">
                      <span className="admin__kpi-label">Active Promos</span>
                      <h3 className="admin__kpi-value">
                        {coupons.filter((c) => c.active).length}
                      </h3>
                      <span className="admin__kpi-trend positive">
                        <i className="ri-flashlight-line"></i> Live at Checkout
                      </span>
                    </div>
                  </div>
                </Col>

                <Col lg="3" sm="6">
                  <div className="admin__kpi-card">
                    <div className="admin__kpi-icon blue">
                      <i className="ri-gift-line"></i>
                    </div>
                    <div className="admin__kpi-info">
                      <span className="admin__kpi-label">Redemptions</span>
                      <h3 className="admin__kpi-value">
                        {coupons.reduce((sum, c) => sum + (Number(c.usageCount) || 0), 0)}
                      </h3>
                      <span className="admin__kpi-trend neutral">
                        <i className="ri-user-heart-line"></i> Loyal Patrons
                      </span>
                    </div>
                  </div>
                </Col>

                <Col lg="3" sm="6">
                  <div className="admin__kpi-card">
                    <div className="admin__kpi-icon amber">
                      <i className="ri-percent-line"></i>
                    </div>
                    <div className="admin__kpi-info">
                      <span className="admin__kpi-label">Max Privilege</span>
                      <h3 className="admin__kpi-value">
                        {Math.max(...coupons.map((c) => (c.type === 'percentage' ? c.value : 0)), 20)}%
                      </h3>
                      <span className="admin__kpi-trend positive">
                        <i className="ri-vip-crown-line"></i> Royal Connoisseur
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="g-4">
                {/* Mint New Voucher Form */}
                <Col lg="5">
                  <div className="admin__chart-panel">
                    <div className="admin__chart-header">
                      <h3 className="admin__chart-title">
                        <i className="ri-add-circle-line" style={{ color: 'var(--admin-gold)' }}></i>
                        Issue New VIP Voucher
                      </h3>
                    </div>

                    <form onSubmit={handleCreateCoupon}>
                      <div className="admin__form-group">
                        <label className="admin__form-label">Voucher Code *</label>
                        <input
                          type="text"
                          className="admin__form-input text-uppercase fw-bold"
                          placeholder="e.g. SUMMER25, CONNOISSEUR"
                          required
                          value={couponFormData.code}
                          onChange={(e) =>
                            setCouponFormData({
                              ...couponFormData,
                              code: e.target.value.toUpperCase().replace(/\s+/g, ''),
                            })
                          }
                        />
                        <small className="text-muted d-block mt-1">
                          Customers will type this exact uppercase string at cart or checkout.
                        </small>
                      </div>

                      <Row>
                        <Col sm="6">
                          <div className="admin__form-group">
                            <label className="admin__form-label">Discount Type *</label>
                            <select
                              className="admin__form-select"
                              value={couponFormData.type}
                              onChange={(e) =>
                                setCouponFormData({ ...couponFormData, type: e.target.value })
                              }
                            >
                              <option value="percentage">Percentage (%)</option>
                              <option value="fixed">Fixed Deduction ($)</option>
                            </select>
                          </div>
                        </Col>

                        <Col sm="6">
                          <div className="admin__form-group">
                            <label className="admin__form-label">
                              {couponFormData.type === 'percentage' ? 'Percentage Rate (%) *' : 'Amount ($ USD) *'}
                            </label>
                            <input
                              type="number"
                              className="admin__form-input"
                              placeholder={couponFormData.type === 'percentage' ? '15' : '50'}
                              required
                              min="1"
                              max={couponFormData.type === 'percentage' ? 95 : 5000}
                              value={couponFormData.value}
                              onChange={(e) =>
                                setCouponFormData({ ...couponFormData, value: e.target.value })
                              }
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col sm="6">
                          <div className="admin__form-group">
                            <label className="admin__form-label">Min Spend ($ USD)</label>
                            <input
                              type="number"
                              className="admin__form-input"
                              placeholder="0 (No minimum)"
                              min="0"
                              value={couponFormData.minOrderAmount}
                              onChange={(e) =>
                                setCouponFormData({ ...couponFormData, minOrderAmount: e.target.value })
                              }
                            />
                          </div>
                        </Col>

                        <Col sm="6">
                          <div className="admin__form-group">
                            <label className="admin__form-label">Max Discount Cap ($)</label>
                            <input
                              type="number"
                              className="admin__form-input"
                              placeholder="e.g. 300"
                              min="1"
                              value={couponFormData.maxDiscount}
                              onChange={(e) =>
                                setCouponFormData({ ...couponFormData, maxDiscount: e.target.value })
                              }
                            />
                          </div>
                        </Col>
                      </Row>

                      <div className="admin__form-group">
                        <label className="admin__form-label">Privilege Description</label>
                        <input
                          type="text"
                          className="admin__form-input"
                          placeholder="e.g. 15% Seasonal Privilege for Haute Living Patrons"
                          value={couponFormData.description}
                          onChange={(e) =>
                            setCouponFormData({ ...couponFormData, description: e.target.value })
                          }
                        />
                      </div>

                      <button type="submit" className="admin__action-btn w-100 justify-content-center py-2 mt-2">
                        <i className="ri-flashlight-line"></i> Mint & Activate Privilege Voucher
                      </button>
                    </form>
                  </div>
                </Col>

                {/* Existing Vouchers Matrix */}
                <Col lg="7">
                  <div className="admin__chart-panel">
                    <div className="admin__chart-header d-flex justify-content-between align-items-center">
                      <h3 className="admin__chart-title mb-0">
                        <i className="ri-file-list-3-line" style={{ color: 'var(--admin-gold)' }}></i>
                        Atelier Vouchers Ledger ({coupons.length})
                      </h3>
                      <button
                        type="button"
                        className="admin__btn-export py-1 px-3"
                        onClick={loadDashboardData}
                        title="Sync vouchers"
                      >
                        <i className="ri-refresh-line"></i> Sync
                      </button>
                    </div>

                    <div className="admin__table-card">
                      <div className="table-responsive">
                        <table className="admin__table">
                          <thead>
                            <tr>
                              <th>Voucher</th>
                              <th>Privilege</th>
                              <th>Threshold</th>
                              <th>Status</th>
                              <th className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {coupons.length === 0 ? (
                              <tr>
                                <td colSpan="5" className="text-center py-4 text-muted">
                                  No promotional vouchers minted yet.
                                </td>
                              </tr>
                            ) : (
                              coupons.map((coupon) => (
                                <tr key={coupon.id}>
                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      <span
                                        className="badge text-dark fw-bold px-2 py-1"
                                        style={{ background: 'var(--admin-gold)', letterSpacing: '0.8px' }}
                                      >
                                        {coupon.code}
                                      </span>
                                    </div>
                                    <div className="small text-muted mt-1" style={{ maxWidth: '180px' }}>
                                      {coupon.description || 'VIP Privilege'}
                                    </div>
                                  </td>
                                  <td>
                                    <strong className="text-white">
                                      {coupon.type === 'percentage'
                                        ? `${coupon.value}% OFF`
                                        : `$${coupon.value} OFF`}
                                    </strong>
                                    {coupon.maxDiscount && coupon.type === 'percentage' && (
                                      <div className="small text-muted">Cap: ${coupon.maxDiscount}</div>
                                    )}
                                  </td>
                                  <td>
                                    <span className="text-muted small">
                                      {Number(coupon.minOrderAmount) > 0
                                        ? `Orders > $${coupon.minOrderAmount}`
                                        : 'No minimum'}
                                    </span>
                                  </td>
                                  <td>
                                    <span
                                      className={`admin__status-badge ${
                                        coupon.active ? 'delivered' : 'pending'
                                      }`}
                                    >
                                      {coupon.active ? '🟢 ACTIVE' : '⏸️ PAUSED'}
                                    </span>
                                  </td>
                                  <td className="text-end">
                                    <div className="d-flex align-items-center justify-content-end gap-1">
                                      <button
                                        type="button"
                                        className="admin__btn-row-action"
                                        onClick={() => handleToggleCoupon(coupon)}
                                        title={coupon.active ? 'Pause voucher' : 'Activate voucher'}
                                      >
                                        <i
                                          className={
                                            coupon.active ? 'ri-pause-circle-line' : 'ri-play-circle-line'
                                          }
                                        ></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="admin__btn-row-action text-danger"
                                        onClick={() => handleDeleteCoupon(coupon)}
                                        title="Delete voucher"
                                      >
                                        <i className="ri-delete-bin-line"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </motion.div>
          )}
        </Container>
      </div>

      {/* ADD / EDIT PIECE MODAL */}
      <AnimatePresence>
        {isAddProductOpen && (
          <div className="admin__modal-backdrop" onClick={() => setIsAddProductOpen(false)}>
            <motion.div
              className="admin__modal-content"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
            >
              <div className="admin__modal-header">
                <h3 className="admin__modal-title">
                  <i className={editingProduct ? "ri-edit-line me-2" : "ri-add-circle-line me-2"}></i>
                  {editingProduct ? 'Edit Atelier Piece' : 'Add New Atelier Piece'}
                </h3>
                <button
                  type="button"
                  className="admin__modal-close"
                  onClick={() => setIsAddProductOpen(false)}
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              <form onSubmit={handleProductSubmit}>
                <div className="admin__form-group">
                  <label className="admin__form-label">Piece Title / Name *</label>
                  <input
                    type="text"
                    className="admin__form-input"
                    placeholder="e.g. Minimalist Travertine Dining Table"
                    required
                    value={productFormData.productName}
                    onChange={(e) =>
                      setProductFormData({ ...productFormData, productName: e.target.value })
                    }
                  />
                </div>

                <Row>
                  <Col md="6">
                    <div className="admin__form-group">
                      <label className="admin__form-label">Category *</label>
                      <select
                        className="admin__form-select"
                        value={productFormData.category}
                        onChange={(e) =>
                          setProductFormData({ ...productFormData, category: e.target.value })
                        }
                      >
                        <option value="sofa">Sofa & Couches</option>
                        <option value="chair">Armchairs & Lounge</option>
                        <option value="table">Dining & Coffee Tables</option>
                        <option value="lighting">Designer Lighting</option>
                        <option value="decor">Home Accents & Mirrors</option>
                      </select>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="admin__form-group">
                      <label className="admin__form-label">Price (USD) *</label>
                      <input
                        type="number"
                        className="admin__form-input"
                        placeholder="e.g. 450"
                        required
                        value={productFormData.price}
                        onChange={(e) =>
                          setProductFormData({ ...productFormData, price: e.target.value })
                        }
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <div className="admin__form-group">
                      <label className="admin__form-label">Noble Material</label>
                      <input
                        type="text"
                        className="admin__form-input"
                        placeholder="e.g. Carrara Marble & Solid Oak"
                        value={productFormData.material}
                        onChange={(e) =>
                          setProductFormData({ ...productFormData, material: e.target.value })
                        }
                      />
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="admin__form-group">
                      <label className="admin__form-label">Available Stock Quantity (Units)</label>
                      <input
                        type="number"
                        min="0"
                        className="admin__form-input"
                        placeholder="e.g. 15"
                        value={productFormData.stockQuantity}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          const qty = isNaN(val) ? 0 : Math.max(0, val);
                          setProductFormData({
                            ...productFormData,
                            stockQuantity: qty,
                            inStock: qty > 0,
                          });
                        }}
                        required
                      />
                    </div>
                  </Col>
                </Row>

                <div className="admin__form-group">
                  <label className="admin__form-label">Short Summary</label>
                  <input
                    type="text"
                    className="admin__form-input"
                    placeholder="Brief description for catalog..."
                    value={productFormData.shortDesc}
                    onChange={(e) =>
                      setProductFormData({ ...productFormData, shortDesc: e.target.value })
                    }
                  />
                </div>

                {/* Enhanced Piece Image: File Upload or Web URL */}
                <div className="admin__form-group">
                  <label className="admin__form-label">
                    <i className="ri-image-add-line me-1" style={{ color: 'var(--admin-gold)' }}></i>
                    Piece Image (Upload File from Device or Enter Web URL)
                  </label>

                  <div className="d-flex flex-column gap-2">
                    {/* File upload button */}
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <input
                        type="file"
                        id="pieceImageFileInput"
                        accept="image/*"
                        className="d-none"
                        onChange={handleProductImageUpload}
                      />
                      <label
                        htmlFor="pieceImageFileInput"
                        className="btn btn-sm btn-outline-warning d-flex align-items-center gap-2 px-3 py-2 cursor-pointer mb-0"
                        style={{ borderRadius: '8px', cursor: 'pointer' }}
                      >
                        <i className="ri-upload-cloud-2-line fs-5"></i>
                        <span>Choose Image from Device</span>
                      </label>
                      <span className="text-muted small">PNG, JPG, WEBP, SVG up to 8MB</span>
                    </div>

                    {/* Or URL Input */}
                    <div className="mt-1">
                      <input
                        type="text"
                        className="admin__form-input"
                        placeholder="Or paste external image link (https://... or data:image/...)"
                        value={productFormData.imgUrl}
                        onChange={(e) =>
                          setProductFormData({ ...productFormData, imgUrl: e.target.value })
                        }
                      />
                    </div>

                    {/* Live Preview Box */}
                    {productFormData.imgUrl && (
                      <div
                        className="d-flex align-items-center justify-content-between p-2 mt-2 rounded"
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(212, 175, 55, 0.4)',
                        }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <img
                            src={productFormData.imgUrl}
                            alt="Preview"
                            style={{
                              width: '56px',
                              height: '56px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              border: '1px solid var(--admin-gold)',
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                          <div>
                            <span className="text-white small fw-bold d-block">Image Preview Active</span>
                            <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                              {productFormData.imgUrl.startsWith('data:')
                                ? 'Uploaded File (Ready to Save)'
                                : 'Web URL Asset'}
                            </span>
                          </div>
                        </div>

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setProductFormData({ ...productFormData, imgUrl: '' })}
                          title="Remove image"
                        >
                          <i className="ri-delete-bin-line"></i> Clear
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-3 mt-4 pt-2 border-top border-secondary">
                  <button
                    type="button"
                    className="btn btn-outline-light"
                    onClick={() => setIsAddProductOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="admin__action-btn">
                    <i className="ri-check-line"></i> {editingProduct ? 'Update Piece' : 'Save to Catalog'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* COURIER DISPATCH MODAL */}
      <AnimatePresence>
        {courierDispatchOrder && (
          <div className="admin__modal-backdrop" onClick={() => setCourierDispatchOrder(null)}>
            <motion.div
              className="admin__modal-content"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
            >
              <div className="admin__modal-header">
                <h3 className="admin__modal-title">
                  <i className="ri-truck-line me-2"></i> Courier Dispatch & Logistics
                </h3>
                <button
                  type="button"
                  className="admin__modal-close"
                  onClick={() => setCourierDispatchOrder(null)}
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              <form onSubmit={handleCourierSubmit}>
                <p className="text-white small mb-3">
                  Assign carrier and tracking reference for commission #{courierDispatchOrder.orderNumber}:
                </p>

                <div className="admin__form-group">
                  <label className="admin__form-label">Courier Partner</label>
                  <select
                    className="admin__form-select"
                    value={courierName}
                    onChange={(e) => setCourierName(e.target.value)}
                  >
                    <option value="Atelier White-Glove">Atelier White-Glove Fleet</option>
                    <option value="Aramex">Aramex Express</option>
                    <option value="Bosta">Bosta Logistics</option>
                    <option value="DHL">DHL Express</option>
                    <option value="FedEx">FedEx International</option>
                  </select>
                </div>

                <div className="admin__form-group">
                  <label className="admin__form-label">Waybill / Tracking Code</label>
                  <input
                    type="text"
                    className="admin__form-input"
                    placeholder="e.g. EG-88349201"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                  />
                </div>

                <div className="d-flex justify-content-end gap-3 mt-4 pt-2 border-top border-secondary">
                  <button
                    type="button"
                    className="btn btn-outline-light"
                    onClick={() => setCourierDispatchOrder(null)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="admin__action-btn">
                    <i className="ri-send-plane-line"></i> Confirm Dispatch & Mark Shipped
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* OFFICIAL ATELIER INVOICE MODAL */}
      <InvoiceModal
        order={selectedInvoiceOrder}
        isOpen={Boolean(selectedInvoiceOrder)}
        onClose={() => setSelectedInvoiceOrder(null)}
      />
    </Helmet>
  );
};

export default AdminDashboard;
